/**
 * SEOApplier — single central component that reads current SEO state from
 * SEOContext (global + pageOverride) and writes to document <head>. Guarantees
 * no duplicate tags and last-mounted-page wins for title/meta.
 */
import { useEffect } from "react";
import { useSEO } from "@/context/SEOContext";
import { useHost } from "@/context/HostContext";

function _abs(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (typeof window === "undefined") return u;
  return `${window.location.origin}${u.startsWith("/") ? "" : "/"}${u}`;
}

function upsertMeta(attr, key, content) {
  if (!content || typeof document === "undefined") return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-managed", "seo");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href || typeof document === "undefined") return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute("data-managed", "seo");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function replaceJsonLdScripts(jsonLdArr) {
  if (typeof document === "undefined") return;
  document.querySelectorAll('script[type="application/ld+json"][data-managed="seo-page"]').forEach((s) => s.remove());
  (jsonLdArr || []).filter(Boolean).forEach((obj) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-managed", "seo-page");
    s.textContent = JSON.stringify(obj);
    document.head.appendChild(s);
  });
}

export default function SEOApplier() {
  const { seo: globalSeo, pageOverride } = useSEO();
  const host = useHost();
  // HOTFIX P0 — SEOApplier hanya menulis <title>/meta storefront pada tenant
  // host. Platform Owner Portal (app.dynicty.com / preview) memakai title
  // dari PlatformBrandingContext (Dynicty) — SEO tenant (Sinar Telur) TIDAK
  // boleh membocor ke tab Platform.
  const isTenantHost = host?.kind === "tenant";

  useEffect(() => {
    if (!isTenantHost) return;
    const override = pageOverride || {};
    const seo = override.seo || globalSeo;

    const finalTitle = override.title || seo.meta.title;
    const finalDesc = override.description || seo.meta.description;
    const finalKw = override.keywords || seo.meta.keywords;
    const finalImg = override.image || seo.og.image;
    const finalOgType = override.ogType || seo.og.type || "website";

    let finalCanonical = override.canonical;
    if (!finalCanonical) {
      if (seo.meta.canonical_base && typeof window !== "undefined") {
        finalCanonical = `${seo.meta.canonical_base}${window.location.pathname}`;
      } else if (typeof window !== "undefined") {
        finalCanonical = window.location.href.split("?")[0];
      }
    }

    const robots = [
      seo.meta.robots_index ? "index" : "noindex",
      seo.meta.robots_follow ? "follow" : "nofollow",
    ].join(",");

    if (finalTitle && typeof document !== "undefined") document.title = finalTitle;
    upsertMeta("name", "description", finalDesc || "");
    if (finalKw) upsertMeta("name", "keywords", finalKw);
    upsertMeta("name", "robots", robots);
    upsertCanonical(finalCanonical);

    upsertMeta("property", "og:title", override.title || seo.og.title || finalTitle);
    upsertMeta("property", "og:description", override.description || seo.og.description || finalDesc || "");
    if (finalImg) upsertMeta("property", "og:image", _abs(finalImg));
    upsertMeta("property", "og:type", finalOgType);
    if (seo.og.site_name) upsertMeta("property", "og:site_name", seo.og.site_name);
    if (finalCanonical) upsertMeta("property", "og:url", finalCanonical);
    upsertMeta("property", "og:locale", "id_ID");

    upsertMeta("name", "twitter:card", seo.twitter.card_type || "summary_large_image");
    upsertMeta("name", "twitter:title", override.title || seo.twitter.title || finalTitle);
    upsertMeta("name", "twitter:description", override.description || seo.twitter.description || finalDesc || "");
    if (finalImg) upsertMeta("name", "twitter:image", _abs(finalImg));

    replaceJsonLdScripts(override.jsonLd);
  }, [globalSeo, pageOverride, isTenantHost]);

  return null;
}
